import { useCallback, useEffect, useRef, useState } from 'react'
import type { BattleInfo, BattleSnapshot, GoalInfo, Hit } from '@boss/shared'
import { apiUrl } from './api'
import { audio } from './audio/audio'
import { DebugPanel, type LocalActions, type LocalOverride } from './debug/DebugPanel'
import { BattleLinks } from './hud/BattleLinks'
import { Feed } from './hud/Feed'
import { HowTo } from './hud/HowTo'
import { Hud } from './hud/Hud'
import { Scene, type SceneHandle } from './scene/Scene'
import { stageIndex } from './scene/config'
import { useBattle } from './state/useBattle'

const showDebug = import.meta.env.DEV || new URLSearchParams(location.search).has('debug')

/** The gate is shown once; afterwards the sound choice is remembered. */
const ENTERED_KEY = 'boss:entered'
const enteredBefore = () => {
  try {
    return localStorage.getItem(ENTERED_KEY) === '1'
  } catch {
    return false
  }
}
const rememberEntered = () => {
  try {
    localStorage.setItem(ENTERED_KEY, '1')
  } catch {
    /* private mode etc. */
  }
}
/** Which side of the desktop layout holds the health card and feed. */
const SIDEBAR_SIDE: 'left' | 'right' = 'left'
/** How many recent attacks the feed keeps; the API sends up to this many on load. */
const FEED_SIZE = 100

export default function App() {
  const [entered, setEntered] = useState(() => enteredBefore())
  const [muted, setMuted] = useState(audio.muted)
  /** Returning visitor wants sound, but the browser needs a tap first. */
  const [needsTap, setNeedsTap] = useState(false)
  const [feed, setFeed] = useState<Hit[]>([])
  const [info, setInfo] = useState<BattleInfo | null>(null)
  const [howTo, setHowTo] = useState(false)
  const goals: GoalInfo[] = info?.goals ?? []
  const sceneRef = useRef<SceneHandle>(null)

  useEffect(() => {
    fetch(apiUrl('/api/goals'), { cache: 'no-store' })
      .then((r) => r.json())
      .then((d: BattleInfo) => {
        setInfo(d)
        document.title = d.ui.title
        audio.setMusic(d.ui.music, d.ui.musicVolume)
        if (enteredBefore() && !audio.muted) {
          void audio.autostart().then((ok) => setNeedsTap(!ok))
        }
      })
      .catch(() => setInfo(null))
  }, [])

  // First interaction anywhere unlocks the remembered "sound on" choice.
  useEffect(() => {
    if (!needsTap) return
    const go = () => {
      void audio.autostart().then((ok) => setNeedsTap(!ok))
    }
    const opts = { once: true, capture: true } as const
    window.addEventListener('pointerdown', go, opts)
    window.addEventListener('keydown', go, opts)
    return () => {
      window.removeEventListener('pointerdown', go, opts)
      window.removeEventListener('keydown', go, opts)
    }
  }, [needsTap])

  const onHit = useCallback((hit: Hit, prev: BattleSnapshot, next: BattleSnapshot) => {
    const goalIndex = Math.max(0, next.goals.findIndex((g) => g.id === hit.goalId))
    const goal = next.goals[goalIndex]
    const stageChanged = !next.defeated && stageIndex(next.progress) !== stageIndex(prev.progress)
    const goalCompleted = goal ? hit.pointsAfter >= goal.target && hit.pointsAfter - hit.points < goal.target : false
    sceneRef.current?.hit(hit, { goalIndex, points: hit.points, superEffective: hit.superEffective, crit: hit.crit, stageChanged, goalCompleted })
    if (stageChanged || goalCompleted) audio.playKo()
    else if (hit.crit) audio.playCrit()
    else if (hit.superEffective) audio.playSuper()
    else audio.playHit(1)
    setFeed((f) => [hit, ...f.filter((x) => x.seq !== hit.seq)].slice(0, FEED_SIZE))
    if (!prev.defeated && next.defeated) {
      sceneRef.current?.victory()
      audio.playVictory()
    }
  }, [])

  const onReset = useCallback(() => {
    sceneRef.current?.reset()
  }, [])

  // First load, battle change, or a jump after a long gap: show the latest attacks straight away.
  const onLoad = useCallback((_s: BattleSnapshot, recent: Hit[]) => {
    setFeed(recent.slice(0, FEED_SIZE))
  }, [])

  const { snapshot: live, transport, error } = useBattle({ onHit, onReset, onLoad })
  const defeatedCopy = info ? { title: info.ui.defeatedTitle, phases: info.ui.defeatedPhases, note: info.ui.defeatedNote } : undefined

  // Debug-only local preview: overrides what this browser shows, never the server.
  const [override, setOverride] = useState<LocalOverride>({})
  const snapshot = applyOverride(live, override)
  const windowInfo = override.window ?? info?.window
  const local: LocalActions = {
    set: (patch) => setOverride((o) => ({ ...o, ...patch })),
    clear: () => {
      setOverride({})
      sceneRef.current?.reset()
    },
    defeat: () => {
      setOverride((o) => ({ ...o, progress: 1, defeated: true }))
      sceneRef.current?.victory()
      audio.playVictory()
    },
    hit: (kind) => {
      const superEffective = kind === 'super' || kind === 'both'
      const crit = kind === 'crit' || kind === 'both'
      const points = (superEffective ? 2 : 1) * (crit ? 2 : 1)
      const goalIndex = Math.floor(Math.random() * Math.max(1, goals.length))
      const fake: Hit = { seq: -Date.now(), eventId: `local-${Date.now()}`, goalId: goals[goalIndex]?.id ?? '', packCode: goals[goalIndex]?.codes[0] ?? '', userRef: 'preview', detail: null, image: null, tier: superEffective ? 'epic' : 'common', superEffective, crit, points, pointsAfter: 0, progressAfter: 0, receivedAt: new Date().toISOString() }
      sceneRef.current?.hit(fake, { goalIndex, points, superEffective, crit, stageChanged: false, goalCompleted: false })
      if (crit) audio.playCrit()
      else if (superEffective) audio.playSuper()
      else audio.playHit(1)
    },
    sampleFeed: () => {
      const now = Date.now()
      const rows: Hit[] = (['both', 'crit', 'super', 'plain'] as const).map((kind, i) => {
        const superEffective = kind === 'super' || kind === 'both'
        const crit = kind === 'crit' || kind === 'both'
        const g = goals[i % Math.max(1, goals.length)]
        return { seq: -(now + i), eventId: `sample-${now}-${i}`, goalId: g?.id ?? '', packCode: g?.codes[0] ?? '', userRef: 'Samp1eWa11etAddr3ss', detail: '2021 #15 Venusaur-Holo PSA 9 Celebrations Classic Collection', image: null, tier: superEffective ? 'epic' : 'common', superEffective, crit, points: (superEffective ? 2 : 1) * (crit ? 2 : 1), pointsAfter: 0, progressAfter: 0, receivedAt: new Date(now - i * 1000).toISOString() }
      })
      setFeed((f) => [...rows, ...f].slice(0, FEED_SIZE))
    },
  }

  const enter = () => {
    audio.unlock()
    // "Enter with sound" means sound, even if a previous visit chose muted.
    audio.setMuted(false)
    setMuted(false)
    void audio.startAmbient()
    rememberEntered()
    setEntered(true)
  }
  const enterMuted = () => {
    audio.setMuted(true)
    setMuted(true)
    rememberEntered()
    setEntered(true)
  }
  const toggleMute = () => {
    audio.unlock()
    if (needsTap) {
      // The click is the unlock the browser was waiting for; keep the remembered "on".
      setNeedsTap(false)
      audio.setMuted(false)
      setMuted(false)
      void audio.startAmbient()
      return
    }
    const next = !muted
    audio.setMuted(next)
    setMuted(next)
    if (!next) void audio.startAmbient()
  }

  return (
    <div className={`app${entered ? '' : ' gated'}`}>
      <main className="arena" data-sidebar={SIDEBAR_SIDE}>
        <div className="stagearea">
          {info ? <BattleLinks ui={info.ui} /> : null}
          <Scene ref={sceneRef} snapshot={snapshot} overlay={<Hud snapshot={snapshot} level={info?.ui.level} window={windowInfo} timeLeftNote={info?.ui.timeLeftNote} defeated={defeatedCopy} className="hud-overlay" />}
            victory={info ? { eyebrow: info.ui.victoryEyebrow, title: info.ui.victoryTitle, subtitle: info.ui.victorySubtitle } : undefined}
            corner={
              info ? (
                <button className="howto-big" onClick={() => setHowTo(true)} aria-haspopup="dialog">
                  <span className="howto-big-mark" aria-hidden="true">
                    ?
                  </span>
                  <span className="howto-big-text">
                    <span className="howto-big-label">How to play</span>
                    <span className="howto-big-sub">Tap for the rules</span>
                  </span>
                </button>
              ) : null
            }
          />
        </div>
        <aside className="sidebar grain">
          <header className="topbar">
            {info?.ui.logo ? (
              <span className="brand">
                <img className="brand-logo" src={info.ui.logo} alt={info.ui.brand} />
                <img className="brand-mark" src={info.ui.mark || info.ui.logo} alt={info.ui.brand} />
              </span>
            ) : (
              <span className="brand">{info?.ui.brand ?? ''}</span>
            )}
            <span className={`transport transport-${transport}`}>{transport === 'ably' || transport === 'relay' ? 'live' : transport}</span>
            <button className="howto-btn" onClick={() => setHowTo(true)} aria-haspopup="dialog" disabled={!info}>
              How to play
            </button>
            <button className={`mute${needsTap ? ' needs-tap' : ''}`} onClick={toggleMute} aria-pressed={muted}>
              {muted ? 'Sound off' : needsTap ? 'Tap for sound' : 'Sound on'}
            </button>
          </header>
          <Hud snapshot={snapshot} level={info?.ui.level} window={windowInfo} timeLeftNote={info?.ui.timeLeftNote} defeated={defeatedCopy} className="hud-stacked" />
          <Feed hits={feed} goals={goals} />
        </aside>
      </main>
      {!entered && (
        <div className="gate" role="dialog" aria-modal="true" aria-labelledby="gate-title">
          <div className="gate-card grain">
            <h1 id="gate-title">{info?.ui.title ?? 'Boss Battle'}</h1>
            <p>{info?.ui.tagline ?? ''}</p>
            <button className="gate-enter" onClick={enter} autoFocus>
              Enter with sound
            </button>
            <button className="gate-quiet" onClick={enterMuted}>
              Enter muted
            </button>
          </div>
        </div>
      )}
      {howTo && info ? <HowTo info={info} onClose={() => setHowTo(false)} /> : null}
      {showDebug && <DebugPanel transport={transport} error={error} goals={goals} snapshot={snapshot} override={override} local={local} />}
    </div>
  )
}

/** Layer the debug preview over the live snapshot. */
function applyOverride(live: BattleSnapshot | null, o: LocalOverride): BattleSnapshot | null {
  if (!live || (o.progress == null && o.defeated == null)) return live
  const progress = o.defeated ? 1 : (o.progress ?? live.progress)
  const goals = live.goals.map((g) => {
    if (o.progress == null && !o.defeated) return g
    const points = Math.round(g.target * progress)
    return { ...g, points, progress: Math.min(1, progress), complete: points >= g.target }
  })
  return { ...live, progress, goals, defeated: o.defeated ?? (progress >= 1 ? live.defeated : false), defeatedAt: o.defeated ? (live.defeatedAt ?? new Date().toISOString()) : live.defeatedAt }
}
